import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiClientService } from '@core/services/api-client.service';
import { SearchCustomer } from '@shared/interfaces/search.interfaces';

export interface CustomerProfile {
  CustomerID: number;
  FirstName: string;
  LastName: string;
  Email: string;
  MDN: string;
  AccountNumber: string;
  EnrollmentNo: string;
  AccountStatus: string;
  IsActive: string;
  ServiceAddress1?: string;
  ServiceAddress2?: string;
  CityName?: string;
  StateCode?: string;
  ZipCode?: string;
  TelephoneNumber?: string;
  EnrollmentType?: string;
  iccid?: string;
}

export interface MinimalProfileData {
  CustomerID: number;
  name: string;
  email: string;
  phone: string;
  accountNumber: string;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerProfileService {
  // Signals for reactive state management
  private currentCustomerIdSignal = signal<number | null>(null);
  private customerProfileSignal = signal<CustomerProfile | null>(null);
  private minimalProfileSignal = signal<MinimalProfileData | null>(null);
  
  // BehaviorSubjects for backward compatibility and complex subscriptions
  private currentCustomerIdSubject = new BehaviorSubject<number | null>(null);
  private customerProfileSubject = new BehaviorSubject<CustomerProfile | null>(null);
  private minimalProfileSubject = new BehaviorSubject<MinimalProfileData | null>(null);
  
  // Public observables
  currentCustomerId$ = this.currentCustomerIdSubject.asObservable();
  customerProfile$ = this.customerProfileSubject.asObservable();
  minimalProfile$ = this.minimalProfileSubject.asObservable();
  
  // Public signals (read-only)
  currentCustomerId = this.currentCustomerIdSignal.asReadonly();
  customerProfile = this.customerProfileSignal.asReadonly();
  minimalProfile = this.minimalProfileSignal.asReadonly();
  
  // Control flags
  isShowAlert = true;
  
  constructor(private apiClient: ApiClientService) {
    this.initializeFromStorage();
  }
  
  /**
   * Initialize customer data from session storage
   */
  private initializeFromStorage(): void {
    try {
      const storedCustomerId = sessionStorage.getItem('CustomerId');
      if (storedCustomerId) {
        const customerId = parseInt(storedCustomerId, 10);
        if (!isNaN(customerId)) {
          this.setCustomerId(customerId);
        }
      }
    } catch (error) {
      console.error('Error initializing customer data from storage:', error);
    }
  }
  
  /**
   * Set the current customer ID
   */
  async setCustomerId(customerId: number): Promise<void> {
    try {
      // Update signals
      this.currentCustomerIdSignal.set(customerId);
      this.currentCustomerIdSubject.next(customerId);
      
      // Store in session storage
      sessionStorage.setItem('CustomerId', customerId.toString());
      
      console.log('Customer ID set:', customerId);
    } catch (error) {
      console.error('Error setting customer ID:', error);
      throw error;
    }
  }
      getCustomerId() {
        return sessionStorage.getItem('CustomerId') ?? '';
    }
  /**
   * Get the current customer ID
   */
  getCurrentCustomerId(): number | null {
    return this.currentCustomerIdSignal();
  }
  
  /**
   * Set customer profile from search result
   */
  setCustomerFromSearchResult(customer: SearchCustomer): void {
    try {
      const profile: CustomerProfile = {
        CustomerID: customer.CustomerID,
        FirstName: customer.FirstName,
        LastName: customer.LastName,
        Email: customer.Email,
        MDN: customer.MDN,
        AccountNumber: customer.AccountNumber,
        EnrollmentNo: customer.EnrollmentNo,
        AccountStatus: customer.AccountStatus,
        IsActive: customer.IsActive,
        ServiceAddress1: customer.ServiceAddress1,
        ServiceAddress2: customer.ServiceAddress2,
        CityName: customer.CityName,
        StateCode: customer.StateCode,
        ZipCode: customer.ZipCode,
        TelephoneNumber: customer.TelephoneNumber,
        EnrollmentType: customer.EnrollmentType,
        iccid: customer.iccid
      };
      
      // Update signals
      this.customerProfileSignal.set(profile);
      this.customerProfileSubject.next(profile);
      
      // Also set customer ID
      this.setCustomerId(customer.CustomerID);
      
      // Generate minimal profile data
      const minimalProfile: MinimalProfileData = {
        CustomerID: customer.CustomerID,
        name: `${customer.FirstName} ${customer.LastName}`.trim(),
        email: customer.Email,
        phone: customer.MDN || customer.TelephoneNumber || '',
        accountNumber: customer.AccountNumber,
        status: customer.AccountStatus
      };
      
      this.minimalProfileSignal.set(minimalProfile);
      this.minimalProfileSubject.next(minimalProfile);
      
      console.log('Customer profile set from search result:', profile);
    } catch (error) {
      console.error('Error setting customer from search result:', error);
      throw error;
    }
  }
  
  /**
   * Load minimal profile data for the current customer
   */
  async getMinimalProfileData(): Promise<MinimalProfileData | null> {
    const customerId = this.getCurrentCustomerId();
    if (!customerId) {
      console.warn('No customer ID available for loading profile data');
      return null;
    }
    
    try {
      // This would typically make an API call to get customer details
      // For now, we'll use the existing profile data if available
      const existingProfile = this.customerProfileSignal();
      if (existingProfile) {
        const minimalProfile: MinimalProfileData = {
          CustomerID: existingProfile.CustomerID,
          name: `${existingProfile.FirstName} ${existingProfile.LastName}`.trim(),
          email: existingProfile.Email,
          phone: existingProfile.MDN || existingProfile.TelephoneNumber || '',
          accountNumber: existingProfile.AccountNumber,
          status: existingProfile.AccountStatus
        };
        
        this.minimalProfileSignal.set(minimalProfile);
        this.minimalProfileSubject.next(minimalProfile);
        
        return minimalProfile;
      }
      
      // TODO: Implement API call to fetch customer details by ID
      // const customerDetails = await this.apiClient.get(`api/Customer/${customerId}`).toPromise();
      // Process and set the customer details
      
      return null;
    } catch (error) {
      console.error('Error loading minimal profile data:', error);
      throw error;
    }
  }
  
  /**
   * Clear customer data
   */
  clearCustomerData(): void {
    this.currentCustomerIdSignal.set(null);
    this.customerProfileSignal.set(null);
    this.minimalProfileSignal.set(null);
    
    this.currentCustomerIdSubject.next(null);
    this.customerProfileSubject.next(null);
    this.minimalProfileSubject.next(null);
    
    sessionStorage.removeItem('CustomerId');
    
    console.log('Customer data cleared');
  }
  
  /**
   * Check if customer data is available
   */
  hasCustomerData(): boolean {
    return this.currentCustomerIdSignal() !== null;
  }
  
  /**
   * Get customer name for display
   */
  getCustomerDisplayName(): string {
    const profile = this.customerProfileSignal();
    if (profile) {
      return `${profile.FirstName} ${profile.LastName}`.trim();
    }
    
    const minimal = this.minimalProfileSignal();
    if (minimal) {
      return minimal.name;
    }
    
    return '';
  }
}
